// Settings > Notifications (/settings/notifications): which workspace alerts a member gets and when
// they stay quiet. Staff notifications are in-app and bcio.chat only: this page never asks for a
// mobile number and never texts a code. The retired SMS opt-in's AlertStatus and Disclosure stay
// exported for existing imports but aren't rendered.
import { useEffect, useState, useSyncExternalStore } from 'react';
import { BellOff, BellRing, ShieldCheck, Smartphone } from 'lucide-react';
import { Icon } from '../../../ui/Icon.tsx';
import { ErrorPanel, SkeletonRows, StatePanel } from '../../../ui/StatePanel.tsx';
import { useWorkspace } from '../../../state/workspace.tsx';
import { Link } from '../../../lib/router.tsx';
import { formatAbsolute, formatPhone } from '../../../lib/format.ts';
import { MessagingClient, type NotificationPreferences } from '../../../lib/api.ts';
import { type MessagingGate, NOTIFICATIONS_PERMISSION } from '../../messaging/model.ts';
import {
  CATEGORY_LABEL, PRIVACY_URL, PreferencesController, type PreferencesSnapshot, SMS_TERMS_URL, STAFF_NOTIFICATION_POLICY, STAFF_SMS_DISCLOSURE,
  STAFF_SMS_DISCLOSURE_VERSION, alertState, browserTimeZone, quietHoursProblem, timeZones,
} from './preferences.ts';

export function NotificationsPage() {
  const ws = useWorkspace();
  const [controller] = useState(() => new PreferencesController(new MessagingClient(ws.api)));
  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);
  useEffect(() => {
    // The own-preferences endpoint applies the effective messaging role policy.
    // Session permission snapshots may omit module-owned defaults; use the
    // authorized read response instead of hiding an already-permitted form.
    void controller.load();
    // Loads once per mount; the controller holds no timers, so there is nothing to stop.
  }, []);
  return <NotificationsView snapshot={snapshot} controller={controller} />;
}

/** The page for one decided state (exported for render tests). */
export function NotificationsView({ snapshot, controller }: { snapshot: PreferencesSnapshot; controller: PreferencesController }) {
  return (
    <div className="page settings notif">
      <p><Link to="/settings" className="btn btn-quiet btn-small notif-back">Back to settings</Link></p>
      <h1 className="page-title">Notifications</h1>
      {snapshot.status === 'error' && snapshot.loadError?.code === 'permission_denied' ? (
        <StatePanel variant="denied" title="You can’t change your notification settings" body="Your role doesn’t include managing your own notifications. Ask an admin if you need it." details={{ permission: NOTIFICATIONS_PERMISSION }} />
      ) : snapshot.status === 'loading' && !snapshot.prefs ? (
        <SkeletonRows rows={4} label="Loading notification settings" />
      ) : snapshot.status === 'error' && snapshot.loadError ? (
        <ErrorPanel error={snapshot.loadError} onRetry={() => void controller.load()} />
      ) : snapshot.prefs ? (
        <Preferences prefs={snapshot.prefs} snapshot={snapshot} controller={controller} />
      ) : null}
    </div>
  );
}

function Preferences({ prefs, snapshot, controller }: { prefs: NotificationPreferences; snapshot: PreferencesSnapshot; controller: PreferencesController }) {
  return (
    <section className="settings-tile settings-wide notif-sms" aria-labelledby="notif-sms-title">
      <h2 id="notif-sms-title" className="section-title">Workspace notification preferences</h2>
      <div className="notice notice-info notif-capability" role="note" aria-label="How notifications are delivered">
        <p><Icon icon={ShieldCheck} /> <strong>In-app and bcio.chat only</strong></p>
        <p>{STAFF_NOTIFICATION_POLICY}</p>
      </div>
      {snapshot.notice ? <p className="meta notif-notice" role="status">{snapshot.notice}</p> : null}
      {snapshot.actionError ? <p className="field-error" role="alert">{snapshot.actionError}</p> : null}
      <Choices key={JSON.stringify([prefs.categories, prefs.quiet_hours])} prefs={prefs} controller={controller} busy={snapshot.busy === 'save'} />
    </section>
  );
}

/** Retired with the SMS opt-in: a member's texting state. Not rendered; kept for existing imports. */
export function AlertStatus({ prefs, gate }: { prefs: NotificationPreferences; gate: MessagingGate }) {
  const state = alertState(prefs);
  const number = prefs.sms_e164 ? formatPhone(prefs.sms_e164) : null;
  const facts: { label: string; value: string }[] = [];
  let headline: string;
  let icon = BellOff;
  switch (state) {
    case 'verified':
      headline = 'On';
      icon = BellRing;
      facts.push({ label: 'Mobile number', value: number ?? '' }, { label: 'Verified', value: formatAbsolute(prefs.sms_verified_at) });
      if (gate.state !== 'enabled') facts.push({ label: 'Sending', value: 'Paused until texting is available' });
      break;
    case 'verified_off':
      headline = 'Off';
      facts.push({ label: 'Mobile number', value: `${number ?? ''} (verified)` }, { label: 'Verified', value: formatAbsolute(prefs.sms_verified_at) });
      break;
    case 'unverified':
      headline = 'Not verified yet';
      icon = Smartphone;
      if (number) facts.push({ label: 'Mobile number', value: number });
      facts.push({ label: 'Alerts', value: 'Not sent until the number is verified' });
      break;
    default:
      headline = 'Off';
  }
  if (prefs.disclosure_version && state !== 'off') facts.push({ label: 'Agreed disclosure', value: prefs.disclosure_version });
  const outdated = (state === 'verified' || state === 'verified_off') && !!prefs.disclosure_version && prefs.disclosure_version !== STAFF_SMS_DISCLOSURE_VERSION;
  return (
    <div className="notif-status">
      <p className="notif-headline"><Icon icon={icon} /> <strong>SMS alerts: {headline}</strong></p>
      {facts.length ? (
        <dl className="facts">
          {facts.map(f => <div key={f.label}><dt>{f.label}</dt><dd className={f.label === 'Agreed disclosure' ? 'mono' : undefined}>{f.value}</dd></div>)}
        </dl>
      ) : null}
      {outdated ? <p className="meta">The terms below (version {STAFF_SMS_DISCLOSURE_VERSION}) changed since you agreed to version {prefs.disclosure_version}.</p> : null}
    </div>
  );
}

/** Retired with the SMS opt-in: the consent text it showed. Not rendered; kept for existing imports. */
export function Disclosure() {
  return (
    <div className="notif-disclosure" id="notif-disclosure" role="group" aria-label="SMS alerts terms">
      {STAFF_SMS_DISCLOSURE.map(p => <p key={p.slice(0, 24)}>{p}</p>)}
      <p>
        <a href={SMS_TERMS_URL} target="_blank" rel="noopener noreferrer">SMS Terms</a>
        <span className="sep" aria-hidden="true" />
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
      </p>
      <p className="meta">Disclosure version <span className="mono">{STAFF_SMS_DISCLOSURE_VERSION}</span></p>
    </div>
  );
}

function Choices({ prefs, controller, busy }: { prefs: NotificationPreferences; controller: PreferencesController; busy: boolean }) {
  const [categories, setCategories] = useState(prefs.categories);
  const [quietOn, setQuietOn] = useState(prefs.quiet_hours !== null);
  const [quiet, setQuiet] = useState(() => prefs.quiet_hours ?? { start: '21:00', end: '08:00', timezone: browserTimeZone() });
  const problem = quietOn ? quietHoursProblem(quiet) : null;
  const zones = timeZones(quiet.timezone);
  const keys = Object.keys(CATEGORY_LABEL) as (keyof NotificationPreferences['categories'])[];
  return (
    <form className="notif-choices" aria-label="Alert choices" onSubmit={e => { e.preventDefault(); if (!problem) void controller.saveChoices(categories, quietOn ? quiet : null); }}>
      <fieldset className="field-group">
        <legend>Notify me about</legend>
        {keys.map(key => (
          <label key={key} className="check">
            <input type="checkbox" checked={categories[key]} onChange={e => setCategories(c => ({ ...c, [key]: e.target.checked }))} aria-describedby={`notif-cat-${key}`} />
            <span>{CATEGORY_LABEL[key].label} <span id={`notif-cat-${key}`} className="meta">{CATEGORY_LABEL[key].hint}</span></span>
          </label>
        ))}
      </fieldset>
      <fieldset className="field-group">
        <legend>Quiet hours</legend>
        <label className="check">
          <input type="checkbox" checked={quietOn} onChange={e => setQuietOn(e.target.checked)} />
          Don’t alert me during quiet hours
        </label>
        {quietOn ? (
          <div className="notif-quiet">
            <div className="field">
              <label htmlFor="notif-quiet-start">From</label>
              <input id="notif-quiet-start" type="time" value={quiet.start} onChange={e => setQuiet(q => ({ ...q, start: e.target.value }))} />
            </div>
            <div className="field">
              <label htmlFor="notif-quiet-end">Until</label>
              <input id="notif-quiet-end" type="time" value={quiet.end} onChange={e => setQuiet(q => ({ ...q, end: e.target.value }))} />
            </div>
            <div className="field">
              <label htmlFor="notif-quiet-tz">Time zone</label>
              <select id="notif-quiet-tz" value={quiet.timezone} onChange={e => setQuiet(q => ({ ...q, timezone: e.target.value }))}>
                {zones.map(z => <option key={z} value={z}>{z}</option>)}
              </select>
            </div>
          </div>
        ) : null}
        {problem ? <p className="field-error" role="alert">{problem}</p> : null}
        <p className="field-hint">Quiet hours apply to the alert categories above.</p>
      </fieldset>
      <div className="form-actions">
        <button type="submit" className="btn" disabled={busy || !!problem} aria-busy={busy || undefined} aria-describedby={problem ? undefined : 'notif-choices-hint'}>{busy ? 'Saving…' : 'Save alert choices'}</button>
      </div>
      <p id="notif-choices-hint" className="meta">These choices apply to your in-app and bcio.chat alerts.</p>
    </form>
  );
}
